<template>
	<div class="t-container page">
        <div class="page-heading" role="heading">
            <h1 class="page-heading__header">
                Fixed Rates
            </h1>
            <div class="t-flex t-mx-auto t-my-2">
				<button :disabled="loadingFuelAdjustment" @click="fuelAdjustmentFormShowing = true" class="t-align-right t-bg-dodger-blue t-px-8 t-py-3 t-mr-2" >
                    <span class="t-text-base t-font-bold t-text-white">
                        Update fuel adjustment factor
						<i v-if="loadingFuelAdjustment" class="fa fa-spinner fa-spin"></i>
						<span v-else class="t-inline-flex t-items-center t-justify-center t-px-2 t-py-1 t-text-xs t-leading-none t-text-black t-bg-white t-rounded-full">
							{{ fuelAdjustmentMultiplier }}%
						</span>
                    </span>
                </button>
                <button @click="onNewFixedRate" class="t-align-right t-bg-dodger-blue t-px-8 t-py-3">
                    <span class="t-text-base t-font-bold t-text-white">
                    <span class="t-text-sm">+</span>
                        Add new fixed rate
                    </span>
                </button>
            </div>
        </div>

		<div class="t-mt-12 t-justify-center">
			<div v-if="!loading && fixedRateOffers.length < 1" class="t-bg-yellow-100 t-border t-w-full t-border-yellow-400 t-text-yellow-700 t-px-4 t-py-3 t-my-3 t-rounded">
				<span class="t-block sm:t-inline">You currently have no fixed rate offers.</span>
			</div>

			<loading-alert class="mt-2" v-if="loading"></loading-alert>

			<div v-if="!loading && fixedRateOffers.length > 0">
				<pagination :data="pagination" v-on:pagination-change-page="fetchFixedRates" :limit="10"></pagination>
				<table class="t-w-full t-text-md bg-white t-shadow-md t-rounded t-mb-4">
					<tbody>
						<tr class="t-border-b">
							<th class="t-text-left t-p-3 t-px-5">ID</th>
							<th class="t-text-left t-p-3 t-px-5">Pickup &amp; Destination</th>
							<th class="t-text-left t-p-3 t-px-5">Rate</th>
							<th class="t-text-left t-p-3 t-px-5">Min Charge</th>
							<th class="t-text-left t-p-3 t-px-5">Service Level</th>
							<th class="t-text-left t-p-3 t-px-5">Package Types</th>
							<th class="t-text-left t-p-3 t-px-5">Live</th>
							<th class="t-text-left t-p-3 t-px-5">Edit</th>
						</tr>
						<fixed-rate-item
							:offer="offer"
							v-for="(offer, index) in fixedRateOffers"
							:key="index"
							@edit="showForm(offer)"
							@delete="showDelete(offer)">
						</fixed-rate-item>
					</tbody>
				</table>
				<pagination :data="pagination" v-on:pagination-change-page="fetchFixedRates" :limit="10"></pagination>
			</div>
		</div>

		<router-view @rateChanged="loadFixedRates()">
		</router-view>

		<fuel-adjustment-form
			:value="fuelAdjustmentMultiplier"
			v-if="fuelAdjustmentFormShowing"
			@cancelled="fuelAdjustmentFormShowing = false"
			@submitted="fuelMultiplierUpdated">
		</fuel-adjustment-form>

		<confirm-delete
			v-if="deleteShowing"
			title="Delete this offer?"
			:text="deleteMessage"
			:deleting="deleting"
			@confirmed="deleteConfirmed()"
			@cancelled="deleteCancelled()">
		</confirm-delete>
	</div>
</template>

<script>
    import FixedRateItem from './FixedRateItem';
	import FuelAdjustmentForm from '../misc/FuelAdjustmentForm';
	import LoadingAlert from '../../utils/LoadingAlert';
	import ConfirmDelete from '../../utils/ConfirmDelete';

    export default {
        name: 'fixed-rates',
        components: {
			FixedRateItem,
			FuelAdjustmentForm,
			LoadingAlert,
			ConfirmDelete
		},
        props: ['business'],

        data() {
            return {
				deleteShowing: false,
				deleting: false,
				rateBeingDeleted: null,
				loading: true,
                fixedRateOffers: [],
				fuelAdjustmentFormShowing: false,
				loadingFuelAdjustment: true,
				fuelAdjustmentMultiplier: null,
                formShowing: false,
				editingRate: null,
                pagination: {}
            }
        },

        mounted() {
            this.loadFixedRates();
			this.loadFuelAdjustmentMultiplier();
        },

        methods: {
			showDelete(rate) {
				this.rateBeingDeleted = rate;
				this.deleteShowing = true;
			},

			deleteConfirmed() {
				this.deleting = true;

				axios.delete("/carry-freight/fixed-rates/"+this.rateBeingDeleted.id)
					 .then(response => {
						 this.deleting = false;
						 this.rateBeingDeleted = null;
						 this.deleteShowing = false;

						this.$swal({
							title: "Fixed rate offer deleted!",
							type: "success",
							confirmButtonText: 'Okay',
						});

						this.loadFixedRates();
					 })
					 .catch(error => {
						 Event.$emit('serverError');
					 });
			},

			deleteCancelled() {
				this.rateBeingDeleted = null;
				this.deleteShowing = false;
			},

			showForm(rate = null) {
				this.formShowing = true;
				this.editingRate = rate;
			},

			fuelMultiplierUpdated() {
				this.fuelAdjustmentFormShowing = false;
				this.loadFuelAdjustmentMultiplier();
			},

			loadFuelAdjustmentMultiplier() {
				this.loadingFuelAdjustment = true;

				axios.get("/carry-freight/settings/fuel-multiplier").then(response => {
					this.fuelAdjustmentMultiplier = response.data.fuel_adjustment_multiplier;
					this.loadingFuelAdjustment = false;
				}).catch(error => {
					Event.$emit('serverError');
				});
			},

            fetchFixedRates(page) {
                if (typeof page === 'undefined') {
                    page = 1;
                }

				this.loading = true;
                axios.get('/carry-freight/fixed-rates?page=' + page, { headers: { content: 'application/json'} })
                    .then(response => {
                        this.fixedRateOffers = response.data.data;

                        this.pagination.current_page = response.data.meta.pagination.current_page;
                        this.pagination.last_page = response.data.meta.pagination.total_pages;
                        this.pagination.next_page_url = response.data.meta.pagination.links.next;
                        this.pagination.per_page = response.data.meta.pagination.per_page;
                        this.pagination.prev_page_url = response.data.meta.pagination.links.previous;
                        this.pagination.total = response.data.meta.pagination.total;

						this.loading = false;
                    })
                    .catch(errors => {
                        Event.$emit('serverError');
                    });
            },

            formCancelled() {
                this.formShowing = false;
				this.editingRate = null;
            },

            formSubmitted() {
                this.formShowing = false;
                this.loadFixedRates();
            },

			loadFixedRates() {
			    this.fetchFixedRates(this.pagination.current_page);
			},

			onNewFixedRate() {
            	this.$router.push({name: 'newFixedRatesForm'});
            },
        },

		computed: {
			deleteMessage() {
				return "Are you sure you want to delete this fixed rate offer?";
			},
		},
    }
</script>
